import { events } from "@constants/tracking";
import HumeAudioAnalyzer from "@features/session/components/HumeAudioAnalyzer";
import useRecorder from "@features/session/hooks/useRecorder";
import { isWeb } from "@helpers/platform";
import { useAppTheme } from "@hooks/useAppTheme";
import usePlayer from "@memorang/features/src/audio/usePlayer";
import { AudioRecorder } from "@memorang/ui";
import { trackCustomEvent } from "analytics";
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from "react";
import { Audio as AudioCompressor } from "react-native-compressor";
import { RecordingLimitAlert } from "./components/RecordingLimitAlert";

type Props = {
	recordedAudio: string | undefined;
	handleSetAudioUri: (uri: string | undefined) => void;
	disabled?: boolean;
	showDiscardButton?: boolean;
	maxDuration?: number;
	label?: string;
	itemId?: number;
	originalItemType?: string;
};

export type AudioRecorderContainerRef = {
	stop: () => Promise<void>;
	isRecording: boolean;
};

const AudioRecorderContainer = forwardRef<AudioRecorderContainerRef, Props>(
	(
		{
			recordedAudio,
			handleSetAudioUri,
			disabled,
			showDiscardButton = true,
			maxDuration = 45,
			label,
			itemId,
			originalItemType,
		},
		ref,
	) => {
		const [limitAlertVisible, setLimitAlertVisible] = useState(false);
		const handleOnRecordingComplete = useCallback(
			async (uri: string) => {
				let finalUri = uri;
				if (!isWeb) {
					const compressedAudio = await AudioCompressor.compress(uri, {
						quality: "medium",
					});
					finalUri = compressedAudio;
				}
				handleSetAudioUri(finalUri);
			},
			[handleSetAudioUri],
		);

		const {
			startRecording,
			stopRecording,
			clearMeterHistory,
			isError,
			isRecording,
			currentTime,
			meterHistory,
			duration,
			unloadRecording,
			audioChunks,
			clearWebAudioChunks,
		} = useRecorder({
			onRecordingComplete: handleOnRecordingComplete,
		});

		useImperativeHandle(
			ref,
			() => ({
				stop: stopRecording,
				isRecording,
			}),
			[stopRecording, isRecording],
		);

		const handleTracking = (status: "start" | "end") => {
			trackCustomEvent({
				eventName:
					status === "start"
						? events.sessionItemResponseAudioRecordingStarted
						: events.sessionItemResponseAudioRecordingEnded,
				itemId: itemId,
				status: status,
			});
		};

		const {
			playSound,
			pauseSound,
			isPlaying = false,
			currentTimeInMilliseconds = 0,
			totalTimeInMilliseconds = 0,
			onSeek,
			isEnded,
			unloadSound,
		} = usePlayer(
			(recordedAudio as string) || "http",
			isWeb ? duration : undefined,
		);

		const currentMode = isRecording || !recordedAudio ? "recording" : "playing";

		useEffect(() => {
			if (isRecording && currentTime > maxDuration * 1000) {
				stopRecording();
				setLimitAlertVisible(true);
			}
		}, [isRecording, currentTime, maxDuration, stopRecording]);

		const handlePlayOrStop = async () => {
			if (isEnded && recordedAudio) {
				await onSeek(0);
				playSound();
			} else if (isPlaying) {
				pauseSound();
			} else if (recordedAudio) {
				playSound();
			}
		};

		const handleRecord = () => {
			if (recordedAudio) {
				handlePlayOrStop();
				return;
			}
			if (isRecording) {
				handleTracking("end");
				stopRecording();
			} else {
				handleTracking("start");
				startRecording(0);
			}
		};

		const handleDelete = useCallback(async () => {
			pauseSound();
			await new Promise((resolve) => setTimeout(resolve, 500)); // wait for audio to pause properly
			await unloadSound();
			unloadRecording();
			handleSetAudioUri(undefined);
			clearMeterHistory();
			clearWebAudioChunks();
		}, [
			pauseSound,
			unloadSound,
			unloadRecording,
			clearMeterHistory,
			handleSetAudioUri,
			clearWebAudioChunks,
		]);

		const icon = useMemo(() => {
			if (isPlaying) {
				return "pause";
			}
			if (isEnded && recordedAudio) {
				return "replay";
			}
			if (recordedAudio) {
				return "play";
			}
			if (isRecording) {
				return "stop";
			}
			if (!recordedAudio && meterHistory.length > 0 && !isError) {
				return "loading";
			}
			return "microphone";
		}, [isPlaying, recordedAudio, isRecording, meterHistory, isError, isEnded]);

		const theme = useAppTheme();
		const bgColor = useMemo(() => {
			if (disabled) {
				return theme.colors.surfaceDisabled;
			}
			return theme.colors.primary;
		}, [disabled, theme]);

		const progress =
			totalTimeInMilliseconds > 0
				? currentTimeInMilliseconds / totalTimeInMilliseconds
				: 0;

		const handleSeek = useCallback(
			async (newProgress: number) => {
				await onSeek(newProgress * totalTimeInMilliseconds);
			},
			[totalTimeInMilliseconds, onSeek],
		);

		return (
			<>
				<AudioRecorder
					disabled={disabled}
					icon={icon}
					handleDelete={handleDelete}
					bgColor={bgColor}
					handleRecord={handleRecord}
					showDelete={!!recordedAudio && showDiscardButton}
					isLoading={icon === "loading"}
					isRecording={isRecording}
					isPlaying={isPlaying}
					meterHistory={meterHistory}
					progress={progress}
					onSeek={handleSeek}
					totalTime={
						currentMode === "recording"
							? maxDuration * 1000
							: totalTimeInMilliseconds
					}
					isPlayingMode={currentMode === "playing"}
					currentTime={
						currentMode === "recording"
							? currentTime
							: currentTimeInMilliseconds
					}
					label={label}
					// originalItemType={originalItemType}
					// ItemId={itemId}
				/>
				<RecordingLimitAlert
					maxDurationSeconds={maxDuration}
					visible={limitAlertVisible}
					onDismiss={() => setLimitAlertVisible(false)}
				/>
				{isWeb && originalItemType === "AUDIO_INPUT_EMOTION" && (
					<HumeAudioAnalyzer
						itemId={itemId?.toString() || ""}
						audioData={
							audioChunks && audioChunks.length > 0
								? audioChunks[audioChunks.length - 1]
								: undefined
						}
						isCleared={audioChunks.length === 0 || !showDiscardButton}
						chunkIndex={audioChunks.length > 0 ? audioChunks.length - 1 : -1}
					/>
				)}
			</>
		);
	},
);

export default AudioRecorderContainer;
